import { OnInit, Component, ViewChild, OnDestroy, Inject } from "@angular/core";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material";
import { Subscription } from 'rxjs';

import { LoginService } from "./login.service";
import { TranslateService } from "../translate/translate.service";
import { SnackbarService } from "../shared/services/snackbar.service";
import { UtilService } from './../shared/services/util.service';

@Component({
    template: require('./login.component.html'),
    styles: [require('./login.component.less')],
    providers: [LoginService, MatDialog, SnackbarService],
    preserveWhitespaces: true,
})
export class LoginComponent implements OnInit, OnDestroy {
    @ViewChild("forgotPassword", { static: false }) forgotPassword: any;
    private _loginData: any = {};
    private _unauthorized = false;
    private _errorMessage: any = {};
    usernameRequired: boolean = false;
    isLoginButtonClicked: boolean = false;
    isUsernameFocus: boolean = false;
    isPasswordFocus: boolean = false;
    loadingLoginPage: boolean = true;
    loadingForgotPassword: boolean = false;

    webSiteUrl: string = 'https://orthocatapult.com/';

    loginSubscription: Subscription;
    forgotPasswordSubscription: Subscription;

    constructor(
        private _loginService: LoginService,
        private _router: Router,
        private _mdlSnackbarService: SnackbarService,
        private _translate: TranslateService,
        @Inject('utilService') private utilService: any
    ) { }

	/**
	 * function invoked for login.
	 * @param  {any} _loginData contains username and password.
	 */
    onSubmitLogin = (_loginData: any) => {
        this.loadingLoginPage =false;
        if (
            typeof _loginData.username === "undefined" ||
            _loginData.username === ""
        ) {
            this.loadingLoginPage = true;
            this._unauthorized = true;
            this._errorMessage = "Login failed, Invalid username or password";
            _loginData.password = "";
        } else if (
            typeof _loginData.password === "undefined" ||
            _loginData.password === ""
        ) {
            this._unauthorized = true;
            this.loadingLoginPage = true;
            this._errorMessage = "Login failed, Invalid username or password";
            _loginData.password = "";
        } else {
            this.isLoginButtonClicked = true;
            this.loginSubscription?.unsubscribe();
            this.loginSubscription = this._loginService.login(_loginData).subscribe(
                (res: any) => this.loginSuccessHandler(res),
                (error) => this.loginError(error)
            );
        }
    };

	/**
	 * function invoked for login success.
	 * @param  {any} res contains api response.
	 */
    loginSuccessHandler = async (res: any) => {
        this.loadingLoginPage = true;
        if (res.status == "ok") {
            const accessTokenData: any = {};
            const roleData: any = {};
            const menuData: any = {};
            const language: any = {};
            let subscription: any = {};
            let country: any = {};

            accessTokenData.token = res.data.token;
            language.lang = res.data.user.language;

            roleData.roles = res.data.user.roles;
            roleData.default_role = roleData.roles.find(
                (roleItem: any) => roleItem.default_role === true
            ).role;
            roleData.selectedRole = roleData.default_role;
            roleData.role = roleData.selectedRole.name;
            roleData.name =
                res.data.user.first_name + " " + res.data.user.last_name;
            roleData.first_name = res.data.user.first_name;
            roleData.id = res.data.user.id;
            roleData.photo = res.data.user.photo;
            roleData.practice_name = res.data.user.practice_name;
            roleData.fromLogin = true;
            roleData.popup = res.data.user.popup;
            menuData.menuList = roleData.selectedRole.menu_list;
            roleData.practice_active = res.data.user.practice_active;

            // user email keys
            roleData.is_ses_email_verified =
                res.data.user.is_ses_email_verified;
            roleData.email = res.data.user.email;
            roleData.fc_email = res.data.user.fc_email;
            roleData.fc_ses_email_verified =
                res.data.user.fc_ses_email_verified;

            // subscription keys
            if (res.data.user.subscription) {
                subscription.package = res.data.user.subscription.package;
                roleData.subscription_status = res.data.user.subscription.status;
                roleData.pending_payment = res.data.user.subscription.pending_payment;
                roleData.expiry_date = res.data.user.subscription.expired_on;
            } else {
                subscription.package = roleData.subscription_status = roleData.pending_payment = null;
            }

            if (res.data.user.address) {
                country.countryKey = res.data.user.address.country_key;
            } else {
                country.countryKey = null;
            }
            await this._loginService.saveToken("practice_country_key", res.data.user.practice_country_key);
            await this._loginService.saveToken("storage_key", accessTokenData);
            await this._loginService.saveToken("role", roleData);
            await this._loginService.saveToken("subscription", subscription);
            await this._loginService.saveToken("menu_list", menuData);
            await this._loginService.saveToken("language", language);
            await this._loginService.saveToken("country", country);

            // to update user roles in base service
            this.utilService.defaultRoleChange.next({ update: true });
            this.utilService.setUserLoggedIn(true);

            this.selectLang(language);
            this._router.navigate(["home/dashboard"]);
            sessionStorage.setItem("logined", "true");
        } else {
        }
    };

    openWebsite() {
        window.open(this.webSiteUrl, '_blank', '');
    }

	/**
	 * funtion invoked for login failure.
	 * @param  {any} error contains api error response.
	 */
    loginError(error: any) {
        this.isLoginButtonClicked = false;
        this.loadingLoginPage = true;
        this._unauthorized = true;
        this._errorMessage = "Login failed, Invalid username or password";
        this._loginData.password = "";
    }

    showForgotPasswordPopup() {
        this.forgotPassword.show();
    }

    // function for forgot password submit
    sendToken(username: any) {
        if (!username) {
            this.usernameRequired = true;
            this._mdlSnackbarService.showSnackbar("FILL_REQUIRED_DETAILS", 'error');
        } else {
            this.loadingForgotPassword = true;
            this.forgotPasswordSubscription?.unsubscribe();
            this.forgotPasswordSubscription = this._loginService.forgotPassword(username).subscribe(
                (res: any) => {
                    this.loadingForgotPassword = false;
                    if (res.status === "ok") {
                        this._mdlSnackbarService.showSnackbar(
                            "PASSWORD_RESET_LINK", 'success'
                        );
                        this.forgotPassword.close();
                    } else {
                        this._mdlSnackbarService.showSnackbar(
                            "INVALID_USERNAME", 'error'
                        );
                    }
                },
                (error) => {
                    this.loadingForgotPassword = false;
                    this._mdlSnackbarService.showSnackbar(
                        "SOMETHING_WENT_WRONG", 'error'
                    )
                }
            );
        }
    }

    cancelForgotPassword() {
        this.forgotPassword.close();
    }

    selectLang(language: any) {
        this._translate.use(language.lang);
    }

    ngOnInit() {
        document.getElementById("username").focus();
    }

    ngOnDestroy() {
        this.forgotPassword.close();
        this.forgotPasswordSubscription?.unsubscribe();
        this.loginSubscription?.unsubscribe();
    }
}
